export type VercelEnvironment = "development" | "preview" | "production";
export type RunEnvironment = "local" | "preview" | "staging" | "production";

export const localPort = process.env.NEXT_PUBLIC_LOCAL_PORT || 9384;
/** The default development URL for the frontend. */
export const localUrl = `http://localhost:${localPort}`;

/**
 * Vercel prefixes some default environment variables with `NEXT_PUBLIC_` for
 * Next.js projects. However, it does not do this during local development. We
 * can't use dynamic checks (e.g. `process.env.${prefix}ENV`) because it's set
 * at build time.
 */

/** The Vercel environment variable for the given environment. */
const vercelEnv = (process.env.NEXT_PUBLIC_VERCEL_ENV ||
  process.env.VERCEL_ENV ||
  "development") as VercelEnvironment;

/** The branch URL for the current environment. */
const currentBranchUrl =
  process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL || process.env.VERCEL_BRANCH_URL;

/**
 * If the current environment is the given environment.
 *
 * https://vercel.com/docs/projects/environment-variables/system-environment-variables
 */
export const isEnvironment = (env: VercelEnvironment) => vercelEnv === env;

/** If this is running in the development (local) environment. */
export const isDevelopment = () => isEnvironment("development");

/** If this is running in a preview environment. Note, this includes staging. */
export const isPreview = () => isEnvironment("preview");

/** If this is running in the staging environment. */
export const isStaging = () =>
  currentBranchUrl === "reporting-git-staging.vercel.zapier-deployment.com";

/** If this is running in the production environment. */
export const isProduction = () => isEnvironment("production");

/** If this is running in the production or staging environment. */
export const isProductionOrStaging = () => isProduction() || isStaging();

/** We set BASE_URL for e2e tests in the CI */
export const hasBaseUrl = () => process.env.BASE_URL || false;

/** The URL that the frontend should use to make requests to the backend. */
export function getBaseUrl() {
  if (isProductionOrStaging()) {
    return (
      process.env.NEXT_PUBLIC_ZAPIER_ORIGIN || "https://zapier-staging.com"
    );
  }

  if (isPreview()) {
    return `https://${currentBranchUrl}`;
  }

  if (hasBaseUrl()) {
    return process.env.BASE_URL;
  }

  return localUrl;
}
